import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import { isUndefined, isNaN } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Autocomplete } from '~/components/Form';
import { t, alturaVolume, validCharacters } from '~/helpers';
import {
  storeArqueacao,
  updateArqueacao,
} from '~/store/modules/arqueacao/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.arqueacao);

  const initials = create ? data : { ...find, ...data };

  async function handleSubmit(param) {
    try {
      let msgItens;

      if (param.itens) {
        msgItens = alturaVolume(
          param.itens,
          param.alturamaximaarqueacao,
          param.volumemaximoarqueacao
        );
      }

      reference.current.setFieldError('idposto', '');
      reference.current.setFieldError('nomearqueacao', '');
      reference.current.setFieldError('alturamaximaarqueacao', '');
      reference.current.setFieldError('volumemaximoarqueacao', '');
      reference.current.setFieldError('itens', '');

      if (!isUndefined(msgItens)) {
        reference.current.setFieldError('itens', t(msgItens));
      } else {
        const schema = Yup.object().shape({
          idposto: Yup.number(t('s.geral.validacao.informeumnumero')).required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          nomearqueacao: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50))
            .test(
              'characters-invalid',
              t('s.configuracao.geral.naopodecontercaracterespecial'),
              (value) => validCharacters(value)
            ),
          alturamaximaarqueacao: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .transform((value) => (isNaN(value) ? undefined : value))
            .integer(t('s.geral.validacao.deveserinteiro'))
            .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
            .lessThan(
              10000000,
              t('s.geral.validacao.valormaximo').replace('%s', 10000000)
            ),
          volumemaximoarqueacao: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .transform((value) => (isNaN(value) ? undefined : value))
            .integer(t('s.geral.validacao.deveserinteiro'))
            .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
            .lessThan(
              10000000,
              t('s.geral.validacao.valormaximo').replace('%s', 10000000)
            ),
          itens: Yup.string(t('s.geral.validacao.informeumnumero')).required(
            t('s.geral.validacao.campoobrigatorio')
          ),
        });

        await schema.validate(param, {
          abortEarly: false,
        });

        const objSend = {
          ...initials,
          ...param,
          idcliente: client,
        };

        if (create) {
          await dispatch(storeArqueacao(objSend, params));
        } else {
          await dispatch(updateArqueacao(objSend, params));
        }
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Autocomplete
            name="idposto"
            label="codigonomeposto"
            url="postoscomboios"
            unique="idposto"
            title={t('s.geral.postocomboio')}
            disabled={!create}
          />
          <Input
            name="nomearqueacao"
            label={t('s.configuracao.arqueacao.nomearqueacao')}
          />
          <Input
            name="alturamaximaarqueacao"
            label={t('s.configuracao.arqueacao.alturamaximaarqueacao')}
            type="number"
          />
          <Input
            name="volumemaximoarqueacao"
            label={t('s.configuracao.arqueacao.volumemaximoarqueacao')}
            type="number"
          />
          <Input
            name="itens"
            label={t('s.configuracao.arqueacao.itens')}
            multiline
            rows="5"
            rowsMax="5"
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
